import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { DownloadIcon1, Download, Download1, Home, Ring, Watch, Tracker, About, DelbandLogo, OriginBadge } from "./icons/icons";
import LanguageToggle from "./LanguageToggle";
import { useTranslation } from "react-i18next";

// Import translations
import fa from "./locales/Header-fa.json";
import en from "./locales/Header-en.json";
import ar from "./locales/Header-ar.json";
import tr from "./locales/Header-tr.json";

const Header: React.FC = () => {
    const { t, i18n } = useTranslation();

    // Register component translations
    useEffect(() => {
        const ns = "Header";
        const resources = { fa, en, ar, tr } as const;

        Object.entries(resources).forEach(([lng, data]) => {
            if (!i18n.hasResourceBundle(lng, ns)) {
                i18n.addResourceBundle(lng, ns, data, true, true);
            }
        });
    }, [i18n]);

    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const hamburgerRef = useRef<HTMLButtonElement>(null);
    const location = useLocation();

    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001,
    });

    // Determine text direction based on current language
    const currentLang = i18n.language;
    const isRTL = ["fa", "ar"].includes(currentLang);
    const dir = isRTL ? "rtl" : "ltr";
    const textAlign = isRTL ? "text-right" : "text-left";

    const iconMap: Record<string, React.ComponentType> = {
        home: Home,
        rings: Ring,
        "smart-bands": Watch,
        hours: Watch,
        tracker: Tracker,
        about: About,
    };

    const navItems = Array.from({ length: 6 }, (_, index) => {
        const item = t(`nav.items.${index}`, { ns: "Header", returnObjects: true }) as {
            id: string;
            label: string;
            path: string;
        };
        return {
            ...item,
            icon: iconMap[item.id] || Home,
        };
    });

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY;
            const isMobile = window.innerWidth < 768;
            if (!isMobile) {
                setIsScrolled(scrollPosition > 10);
            } else {
                setIsScrolled(false);
            }
        };

        const handleResize = () => {
            const isMobile = window.innerWidth < 768;
            if (isMobile) {
                setIsScrolled(false);
            } else {
                const scrollPosition = window.scrollY;
                setIsScrolled(scrollPosition > 10);
            }
        };

        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && mobileMenuOpen) {
                setMobileMenuOpen(false);
                hamburgerRef.current?.focus();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [mobileMenuOpen]);

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const handleNavItemClick = () => {
        setMobileMenuOpen(false);
        hamburgerRef.current?.focus();
    };

    const handleOverlayClick = () => {
        setMobileMenuOpen(false);
        hamburgerRef.current?.focus();
    };

    const handleComingSoonClick = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    return (
        <header dir={dir} className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? "bg-white shadow-md" : "md:bg-white"}`}>
            {/* دسکتاپ */}
            <div className="container mx-auto px-4 hidden md:flex items-center justify-between h-20 py-2">
                <Link
                    to="/biolink"
                    className="group relative flex gap-2.5 items-center p-1.5 -m-1.5 rounded-2xl hover:bg-blue-50/70 transition-all duration-300 cursor-pointer select-none"
                    aria-label={t("logo.alt", { ns: "Header", defaultValue: "Delband BioLink" })}
                >
                    <motion.div
                        className="relative flex items-center justify-center"
                        whileHover={{ scale: 1.1, rotate: [0, -4, 4, 0] }}
                        whileTap={{ scale: 0.93 }}
                        transition={{
                            scale: { type: "spring", stiffness: 400, damping: 15 },
                            rotate: { duration: 0.35, ease: "easeInOut" },
                        }}
                    >
                        <div className="absolute inset-0 bg-blue-500/25 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                        <DelbandLogo lang={currentLang} />
                    </motion.div>
                    <div className="flex flex-col">
                        <div className="flex items-center gap-1.5">
                            <OriginBadge lang={currentLang} className="w-12 h-6 object-contain group-hover:brightness-110 transition-all duration-200" />
                            <motion.span
                                initial={{ opacity: 0, scale: 0.8 }}
                                whileHover={{ scale: 1.05 }}
                                className="opacity-0 group-hover:opacity-100 translate-y-0.5 group-hover:translate-y-0 transition-all duration-300 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 shadow-sm border border-blue-200/70 whitespace-nowrap"
                            >
                                {isRTL ? "بیولینک" : "BioLink"}
                            </motion.span>
                        </div>
                        <p className="text-[#5D697F] group-hover:text-blue-600 text-sm transition-colors duration-200">{t("logo.tagline", { ns: "Header" })}</p>
                    </div>
                </Link>
                <nav className="flex items-center h-full gap-1">
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.id}
                                to={item.path}
                                className={`relative mx-1 text-base font-medium px-3.5 py-2 rounded-xl flex items-center justify-center transition-all duration-200
                    ${isActive ? "bg-gradient-to-t from-[#FFFFFF00] to-[#E0EDFF] text-[#155DFC] font-semibold" : "text-[#5D697F] hover:text-[#155DFC] hover:bg-blue-50/50"}`}
                            >
                                <motion.span
                                    className="relative flex items-center justify-center leading-none"
                                    whileHover={isActive ? {} : "hover"}
                                    initial="initial"
                                    variants={{
                                        initial: { color: isActive ? "#155DFC" : "#5D697F" },
                                        hover: { color: "#155DFC" },
                                    }}
                                    transition={{ duration: 0.2 }}
                                >
                                    {item.label}
                                </motion.span>
                           
                            </Link>
                        );
                    })}
                </nav>
                <div className="flex items-center h-full gap-4">
                    <LanguageToggle />

                    <Link to="/download" className="flex items-center justify-center bg-[#171F35] text-white px-5 py-2.5 rounded-2xl text-base font-bold shadow hover:bg-[#0F172A] hover:scale-105 transition-all duration-200 whitespace-nowrap leading-none group">
                        <span className={`flex items-center justify-center shrink-0 ${isRTL ? "ml-2.5" : "mr-2.5"}`}>
                            <Download />
                        </span>
                        <span className="flex items-center leading-none">
                            {t("buttons.download", { ns: "Header" })}
                        </span>
                    </Link>
                </div>
            </div>

            {/* موبایل */}
            <div className="md:hidden fixed  left-0 right-0 flex items-center justify-between h-16 px-4 mt-5 mx-3 bg-white rounded-2xl shadow-[0_2px_6px_rgba(0,0,0,0.08)] z-30   ">
                <motion.button
                    ref={hamburgerRef}
                    className="p-2 rounded-full hover:bg-[#E0EDFF] transition-all duration-200"
                    onClick={toggleMobileMenu}
                    aria-controls="mobile-drawer"
                    aria-expanded={mobileMenuOpen}
                    aria-label={t("aria.mobileMenu", { ns: "Header" })}
                    whileTap={{ scale: 0.9 }}
                    animate={{
                        rotate: mobileMenuOpen ? 90 : 0,
                        transition: { duration: 0.3 },
                    }}
                    type="button"
                >
                    <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="#002B5C"
                        viewBox="0 0 24 24"
                    >
                        {mobileMenuOpen ? (
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        ) : (
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        )}
                    </svg>
                </motion.button>

                <Link
                    to="/biolink"
                    className="group relative flex items-center justify-center p-1 rounded-xl active:scale-95 transition-transform duration-200"
                    aria-label={t("logo.alt", { ns: "Header", defaultValue: "Delband BioLink" })}
                >
                    <motion.div
                        className="relative flex items-center justify-center"
                        whileHover={{ scale: 1.08 }}
                        whileTap={{ scale: 0.92 }}
                        transition={{ type: "spring", stiffness: 400, damping: 15 }}
                    >
                        <div className="absolute inset-0 bg-blue-500/15 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                        <img src="../../media/logo-brand.svg" alt={t("logo.alt", { ns: "Header" })} className="w-24 h-auto group-hover:brightness-110 transition-all duration-200" />
                    </motion.div>
                </Link>

                <Link
                    to="/download"
                    className="p-2 rounded-xl hover:bg-[#E0EDFF]/80 active:scale-95 transition-all duration-200 flex items-center justify-center group"
                    aria-label={t("aria.downloadLabel", { ns: "Header", defaultValue: "دانلود اپلیکیشن دلبند" })}
                >
                    <Download1 className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
                </Link>
            </div>

            <div className="md:hidden h-20 "></div>

            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        className="fixed inset-0 bg-black/60 z-20 md:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={handleOverlayClick}
                    />
                )}
            </AnimatePresence>

            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        ref={menuRef}
                        className={`fixed ${isRTL ? "right-3" : "left-3"} my-5 py-3 w-64 bg-white rounded-2xl shadow-[0_2px_6px_rgba(0,0,0,0.08)] z-50`}
                        role="navigation"
                        aria-label={t("aria.mobileMenuLabel", { ns: "Header" })}
                        initial={{ x: isRTL ? "100%" : "-100%", opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: isRTL ? "100%" : "-100%", opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.2}
                        onDragEnd={(_, info) => {
                            if ((isRTL && info.offset.x > 100) || (!isRTL && info.offset.x < -100)) {
                                setMobileMenuOpen(false);
                            }
                        }}
                    >
                        <div className="p-4 flex flex-col h-full">
                            <nav className="space-y-1.5 mb-6">
                                {navItems.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <Link
                                            key={item.id}
                                            to={item.path}
                                            onClick={handleNavItemClick}
                                            className={`group flex items-center w-full ${textAlign} rounded-xl text-[1rem] font-medium transition-all duration-200
                        ${location.pathname === item.path ? "text-[black] bg-gradient-to-t from-[#FFFFFF00] to-[#E0EDFF]" : "text-[#5D697F] hover:text-[#155DFC]"}`}
                                        >
                                            <motion.div
                                                whileHover={{
                                                    x: isRTL ? -4 : 4,
                                                }}
                                                whileTap={{ scale: 0.98 }}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: 0.05 * index,
                                                }}
                                                className={`group flex items-center w-full ${textAlign} py-2.5 px-3 rounded-xl text-[1rem] font-medium transition-all duration-200
                                          ${location.pathname === item.path ? "text-[#155DFC] font-semibold bg-gradient-to-t from-[#FFFFFF00] to-[#E0EDFF]" : "text-[#5D697F] hover:text-[#155DFC]"}`}
                                            >
                                                <div className={`w-5 h-5 flex items-center justify-center shrink-0 ${isRTL ? "ml-2.5" : "mr-2.5"}`}>
                                                    <Icon />
                                                </div>

                                                <span className="flex items-center leading-none">{item.label}</span>
                                                {location.pathname === item.path && (
                                                    <motion.div
                                                        className={`w-1.5 h-1.5 rounded-full bg-[#155DFC] ${isRTL ? "mr-auto" : "ml-auto"}`}
                                                        layoutId="activeNavDot"
                                                        initial={{ scale: 0 }}
                                                        animate={{ scale: 1 }}
                                                        transition={{
                                                            type: "spring",
                                                            stiffness: 500,
                                                            damping: 30,
                                                        }}
                                                    />
                                                )}
                                                <motion.div
                                                    className={`absolute ${isRTL ? "right-0" : "left-0"} top-0 bottom-0 w-1 bg-[#155DFC] ${
                                                        isRTL ? "rounded-r-full" : "rounded-l-full"
                                                    } opacity-0 group-hover:opacity-100`}
                                                    initial={{ height: 0 }}
                                                    whileHover={{ height: "100%" }}
                                                    transition={{ duration: 0.2 }}
                                                />
                                            </motion.div>
                                        </Link>
                                    );
                                })}
                                <LanguageToggle />
                            </nav>

                            <motion.div className="mt-auto" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                                <Link
                                    to="/download"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="relative overflow-hidden bg-gradient-to-r from-[#0038A8] via-[#0072CE] to-[#155DFC] text-white px-6 py-3.5 rounded-xl text-base font-bold shadow-md shadow-blue-500/25 text-center w-full flex items-center justify-center gap-2.5 leading-none hover:shadow-lg hover:shadow-blue-500/35 active:scale-[0.98] transition-all duration-200"
                                >
                                    <span className="flex items-center justify-center shrink-0">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                        </svg>
                                    </span>
                                    <span className="flex items-center leading-none">
                                        {t("buttons.download", { ns: "Header", defaultValue: "دانلود دلبند" })}
                                    </span>
                                </Link>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div className={`h-0.5 bg-gradient-to-${isRTL ? "l" : "r"} from-[#3B82F6] to-[#155DFC] ${isRTL ? "origin-right" : "origin-left"} hidden md:block`} style={{ scaleX }} />
        </header>
    );
};

export default Header;
